import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  ShoppingCart,
  CreditCard,
  Star,
  Plus,
  Minus
} from 'lucide-react'

const StoreCheckoutPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [quantity, setQuantity] = useState(1)

  const productId = searchParams.get('product') || '1'

  const products = {
    '1': {
      name: "Apple AirPods Pro (2nd Gen)",
      price: 249.99,
      originalPrice: 299.99,
      image: "https://images.unsplash.com/photo-1600294037681-c80b4cb5b434?w=300&h=300&fit=crop",
      rating: 4.9,
      reviews: 2847
    },
    '2': {
      name: "Samsung Galaxy Watch 6",
      price: 329.99,
      originalPrice: 399.99,
      image: "https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=300&h=300&fit=crop",
      rating: 4.7,
      reviews: 1892
    },
    '3': {
      name: "MacBook Air M3 13-inch",
      price: 1299.99,
      originalPrice: 1499.99,
      image: "https://images.unsplash.com/photo-1541807084-5c52b6b3adef?w=300&h=300&fit=crop",
      rating: 4.8,
      reviews: 3634
    },
    '4': {
      name: "iPhone 15 Pro Max",
      price: 1199.99,
      originalPrice: 1299.99,
      image: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=300&h=300&fit=crop",
      rating: 4.9,
      reviews: 5445
    },
    '5': {
      name: "Sony WH-1000XM5 Headphones",
      price: 379.99,
      originalPrice: 429.99,
      image: "https://images.unsplash.com/photo-1546435770-a3e426bf472b?w=300&h=300&fit=crop",
      rating: 4.8,
      reviews: 2723
    },
    '6': {
      name: "iPad Pro 12.9-inch M4",
      price: 1099.99,
      originalPrice: 1199.99,
      image: "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=300&h=300&fit=crop",
      rating: 4.9,
      reviews: 1556
    },
    '7': {
      name: "Gaming Mechanical Keyboard",
      price: 159.99,
      originalPrice: 199.99,
      image: "https://images.unsplash.com/photo-1541140532154-b024d705b90a?w=300&h=300&fit=crop",
      rating: 4.6,
      reviews: 892
    },
    '8': {
      name: "4K Webcam for Streaming",
      price: 129.99,
      originalPrice: 169.99,
      image: "https://images.unsplash.com/photo-1587831990711-23ca6441447b?w=300&h=300&fit=crop",
      rating: 4.5,
      reviews: 634
    },
    '9': {
      name: "Wireless Charging Station",
      price: 79.99,
      originalPrice: 99.99,
      image: "https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=300&h=300&fit=crop",
      rating: 4.4,
      reviews: 445
    }
  }

  const product = products[productId as keyof typeof products] || products['1']

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  }

  const handleCompleteOrder = () => {
    navigate(`/order-confirmation?product=${productId}`)
  }

  const incrementQuantity = () => setQuantity(prev => prev + 1)
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1))

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Navigation */}
      <nav className="p-6 bg-white shadow-sm">
        <div className="container mx-auto flex items-center justify-between">
          <Link
            to="/product-selection"
            className="inline-flex items-center space-x-2 text-[#6E6D7A] hover:text-[#3B82F6] transition-colors duration-200 font-sans"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Products</span>
          </Link>
          
          <div className="flex items-center space-x-2">
            <ShoppingCart className="w-6 h-6 text-[#FE4215]" />
            <span className="text-[#1A2B49] font-medium font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              4Sale Store
            </span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-4xl mx-auto"
        >
          {/* Page Title */}
          <motion.h1
            variants={itemVariants}
            className="text-3xl font-bold mb-8 text-[#1A2B49] font-sans"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Checkout
          </motion.h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Product Details */}
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-lg p-6"
              style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', borderRadius: '12px' }}
            >
              <h2 className="text-xl font-bold mb-6 text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                Order Summary
              </h2>
              
              <div className="flex items-center space-x-4 mb-6">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h3 className="font-medium text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {product.name}
                  </h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
                      ))}
                    </div>
                    <span className="text-sm text-[#6E6D7A] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                      ({product.reviews} reviews)
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className="text-lg font-bold text-[#FE4215] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                      ${product.price}
                    </span>
                    <span className="text-sm text-[#6E6D7A] line-through font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                      ${product.originalPrice}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-[#1A2B49] font-medium font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Quantity:
                </span>
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={decrementQuantity}
                    className="w-8 h-8 rounded-full bg-[#F3F3F3] flex items-center justify-center hover:bg-[#E5E5E5] transition-colors"
                  >
                    <Minus className="w-4 h-4 text-[#6E6D7A]" />
                  </button>
                  <span className="w-8 text-center font-medium text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {quantity}
                  </span>
                  <button 
                    onClick={incrementQuantity}
                    className="w-8 h-8 rounded-full bg-[#F3F3F3] flex items-center justify-center hover:bg-[#E5E5E5] transition-colors"
                  >
                    <Plus className="w-4 h-4 text-[#6E6D7A]" />
                  </button>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between mb-2">
                  <span className="text-[#6E6D7A] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Subtotal:
                  </span>
                  <span className="text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    ${(product.price * quantity).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-[#6E6D7A] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Shipping:
                  </span>
                  <span className="text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Free
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                  <span className="text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Total:
                  </span>
                  <span className="text-[#FE4215] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    ${(product.price * quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Payment Form */}
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-lg p-6"
              style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', borderRadius: '12px' }}
            >
              <h2 className="text-xl font-bold mb-6 text-[#1A2B49] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                Payment Information
              </h2>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2B49] mb-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Email Address
                  </label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FE4215] focus:border-transparent font-sans"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2B49] mb-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Card Number
                  </label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FE4215] focus:border-transparent font-sans"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    placeholder="1234 5678 9012 3456"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2B49] mb-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Expiry Date
                    </label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FE4215] focus:border-transparent font-sans"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      placeholder="MM/YY"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2B49] mb-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                      CVV
                    </label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FE4215] focus:border-transparent font-sans"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      placeholder="123"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2B49] mb-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Full Name
                  </label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FE4215] focus:border-transparent font-sans"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    placeholder="John Doe"
                  />
                </div>

                <button 
                  type="button"
                  onClick={handleCompleteOrder}
                  className="w-full inline-flex items-center justify-center space-x-3 px-8 py-4 bg-[#FE4215] text-white rounded-lg hover:bg-[#E53910] transition-all duration-300 font-medium text-lg font-sans"
                  style={{ 
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '8px'
                  }}
                >
                  <CreditCard className="w-6 h-6" />
                  <span>Complete Order</span>
                </button>
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default StoreCheckoutPage
